import Link from '@/routing/Link'
import { useCallback, useEffect, useRef, useState } from 'react'
import { usePathname, useRouter } from '@/routing/navigation'
import {
  AlertCircle,
  CheckCircle2,
  Loader2,
  RefreshCcw,
  Trash2,
} from '@digital-twins/design-system/icons'
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert'
import { SessionExpirySnackbar } from '../../components/SessionExpirySnackbar'
import { Button } from '@/components/ui/button'
import {
  AUDIENCE_WIZARD_BUTTON_CLASS,
  AUDIENCE_WIZARD_BUTTON_VARIANT,
} from '@/lib/constants/audience-wizard'
import { Skeleton } from '@/components/ui/skeleton'
import { Text } from '@/components/ui/Text'
import { ImportStepHeader } from '../../components/ImportStepHeader'
import { SimulatingProgressCard } from '../../components/SimulatingProgressCard'
import { DiscardImportSessionDialog } from '../../components/DiscardImportSessionDialog'
import { useSimulatingProgress } from '../../hooks/useSimulatingProgress'
import { useImportWizardContext } from '../../context/ImportWizardContext'
import { useAudienceImportSessionMutations } from '../../hooks/useAudienceImportSession'
import {
  clearPendingCreateIntent,
  readPendingCreateIntent,
} from '@/layouts/AudienceImportSessionLayout/lib/import-session-route-handoff'
import { useStatusProjection } from '../../hooks/useStatusProjection'
import { audienceImportSessionsApi } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/get-api-error-message'
import { useTranslation } from 'react-i18next'
import {
  completeAudienceHandoff,
  startAudienceHandoff,
} from '../../lib/import-session-client-metrics'
import {
  navigateToImportRoute,
  warmImportRouteCaches,
} from '../../lib/import-session-route-handoff'
import type { AudienceImportRouteAck } from '@digital-twins/shared-types'


function firstNonEmptyMessage(
  ...candidates: Array<string | null | undefined>
): string | undefined {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value.trim()
    }
  }
  return undefined
}

export function StatusStep() {
  const { t } = useTranslation('app')
  const { sessionId, snapshot } = useImportWizardContext()
  const router = useRouter()
  const pathname = usePathname()

  const {
    error,
    createAudience,
    isCreating,
    isDiscarding,
    retryCreate,
    discard,
    lastMutationRequestId,
  } = useAudienceImportSessionMutations(sessionId, {
    fallbackMachineVersion: snapshot?.machineVersion,
  })
  const {
    projection,
    isLoading: isProjectionLoading,
    error: projectionError,
    isRealtimeConnected,
  } = useStatusProjection(sessionId, {
    lastMutationRequestId,
  })
  const [actionError, setActionError] = useState<string | null>(null)
  const [discardDialogOpen, setDiscardDialogOpen] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const createIntentStartedRef = useRef(false)
  const createIntentFiltersRef = useRef<string[]>([])
  const createIntentCustomLabelsRef = useRef<Record<string, string>>({})
  const createIntentAudienceTagsRef = useRef<unknown[]>([])
  const flowSnapshot = snapshot ?? projection?.flowSnapshot ?? null
  const flowActions = flowSnapshot?.actions
  const wizardPhase = flowSnapshot?.wizardPhase ?? null
  const previewBranch = flowSnapshot?.branchSummaries.preview
  const previewReview = previewBranch?.review ?? null
  const createBranch = flowSnapshot?.branchSummaries.create
  const createState = createBranch?.state ?? 'not_started'
  const createSummaryMessage = createBranch?.summaryMessage ?? null
  const createErrorMessage = createBranch?.errorMessage ?? null
  const createQueuedAtMs = createBranch?.queuedAt
    ? new Date(createBranch.queuedAt).getTime()
    : null
  const createStartedAtMs = createBranch?.startedAt
    ? new Date(createBranch.startedAt).getTime()
    : null
  const audienceDetailRoute = createBranch?.audienceDetailRoute ?? null
  const isExpired = flowSnapshot?.machineState === 'expired'
  const isPreviewFailed = previewBranch?.state === 'failed'
  const isEnrichmentFailed =
    previewReview?.featuredTwinEnrichmentStatus === 'failed'
  const previewSummaryMessage = previewBranch?.summaryMessage ?? null
  const isCreateInProgress =
    createState === 'queued' ||
    createState === 'creating' ||
    createState === 'finalizing'
  /**
   * Backend can leave a queued/creating row while dataset preflight runs or while
   * the wizard is still on identity/segmentation — showing "Create is taking longer…"
   * and building progress from that row is misleading.
   */
  const staleCreateRunVersusWizard = Boolean(
    flowSnapshot &&
      (flowSnapshot.preflightStatus === 'pending' ||
        (flowSnapshot.wizardPhase != null &&
          flowSnapshot.wizardPhase !== 'create_running' &&
          flowSnapshot.wizardPhase !== 'create_failed' &&
          flowSnapshot.wizardPhase !== 'completed')),
  )
  const isCreateInProgressForUi =
    isCreateInProgress && !staleCreateRunVersusWizard
  const isReviewPreparationInProgress =
    wizardPhase === 'analyze_running' ||
    wizardPhase === 'preview_hydrating' ||
    wizardPhase === 'preview_preparing' ||
    (!wizardPhase &&
      Boolean(previewReview) &&
      !previewReview?.isReady &&
      !isCreateInProgressForUi &&
      !isExpired &&
      !isPreviewFailed)
  const isCreateCompleted =
    createState === 'completed' && Boolean(audienceDetailRoute)
  const isCreateFailed = createState === 'failed'
  const expiresAtMs = projection?.expiresAt ? new Date(projection.expiresAt).getTime() : null
  const msUntilExpiry = expiresAtMs != null ? expiresAtMs - now : null
  const isExpiringSoon =
    msUntilExpiry != null && msUntilExpiry > 0 && msUntilExpiry <= 15 * 60 * 1000
  const isSimulating =
    isCreateInProgressForUi || isReviewPreparationInProgress
  const [progressFallbackStartMs, setProgressFallbackStartMs] = useState<number | null>(null)
  useEffect(() => {
    if (!isSimulating) {
      setProgressFallbackStartMs(null)
      return
    }
    if (createStartedAtMs != null || createQueuedAtMs != null) return
    setProgressFallbackStartMs((prev) => prev ?? Date.now())
  }, [isSimulating, createStartedAtMs, createQueuedAtMs])
  const progressStartMs =
    createStartedAtMs ?? createQueuedAtMs ?? progressFallbackStartMs
  const simulatingPercent = useSimulatingProgress(isSimulating, progressStartMs)

  useEffect(() => {
    if (!sessionId || !createBranch?.audienceId || !audienceDetailRoute) return
    if (createState !== 'completed') return
    completeAudienceHandoff({
      sessionId,
      audienceId: createBranch.audienceId,
      targetRoute: audienceDetailRoute,
    })
  }, [audienceDetailRoute, createBranch?.audienceId, createState, sessionId])

  useEffect(() => {
    if (createState !== 'completed' || !audienceDetailRoute) return
    router.push(audienceDetailRoute)
  }, [createState, audienceDetailRoute, router])

  useEffect(() => {
    const id = window.setInterval(() => {
      setNow(Date.now())
    }, 60_000)
    return () => window.clearInterval(id)
  }, [])

  const startCreateFromStatus = useCallback(
    async (activeFilterColumns?: string[], customLabels?: Record<string, string>, audienceTags?: unknown[]) => {
      setActionError(null)
      try {
        await createAudience({ activeFilterColumns, customLabels, audienceTags })
      } catch (createError) {
        setActionError(
          getApiErrorMessage(createError, 'Audience creation failed.'),
        )
      }
    },
    [createAudience],
  )

  useEffect(() => {
    if (!sessionId || !projection || createIntentStartedRef.current) {
      return
    }

    const intent = readPendingCreateIntent(sessionId)
    if (isCreateCompleted) {
      clearPendingCreateIntent(sessionId)
      createIntentStartedRef.current = true
      return
    }
    if (!intent) {
      return
    }
    createIntentStartedRef.current = true
    createIntentFiltersRef.current = intent.activeFilterColumns
    createIntentCustomLabelsRef.current = intent.customLabels ?? {}
    createIntentAudienceTagsRef.current = intent.audienceTags ?? []
    clearPendingCreateIntent(sessionId)
    void startCreateFromStatus(intent.activeFilterColumns, intent.customLabels, intent.audienceTags)
  }, [
    isCreateCompleted,
    projection,
    sessionId,
    startCreateFromStatus,
  ])

  async function handleDiscard() {
    setActionError(null)
    try {
      await discard()
      setDiscardDialogOpen(false)
      router.push('/audiences/new')
    } catch (discardError) {
      setActionError(
        getApiErrorMessage(
          discardError,
          'Failed to discard the import session.',
        ),
      )
    }
  }

  const [isRetryingEnrichment, setIsRetryingEnrichment] = useState(false)

  const handleRetryEnrichment = useCallback(async () => {
    if (!sessionId) return
    setIsRetryingEnrichment(true)
    setActionError(null)
    try {
      await audienceImportSessionsApi.beginPreviewEnrichment(sessionId)
    } catch (err) {
      setActionError(
        getApiErrorMessage(err, 'Failed to retry enrichment. Please contact support.'),
      )
    } finally {
      setIsRetryingEnrichment(false)
    }
  }, [sessionId])

  if (isProjectionLoading && !projection) {
    return (
      <>
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-5 w-72" />
        <Skeleton className="h-[200px] rounded-2xl" />
      </>
    )
  }

  const loadError = error ?? projectionError

  if (loadError || !projection || !flowSnapshot) {
    return (
      <>
        <ImportStepHeader title={t('audienceCreate.statusUnavailable')} helper={t('audienceCreate.statusUnavailableHelper')} />
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t('audienceCreate.sessionUnavailable')}</AlertTitle>
          <AlertDescription>
            {loadError instanceof Error
              ? getApiErrorMessage(
                  loadError,
                  t('audienceCreate.sessionExpiredOrDiscarded'),
                )
              : t('audienceCreate.sessionExpiredOrDiscarded')}
          </AlertDescription>
        </Alert>
        <Button asChild variant="outline" shape="pill" className="self-center">
          <Link href="/audiences/new">{t('audienceCreate.startOver')}</Link>
        </Button>
      </>
    )
  }

  const analyzeFailureDefault = t('audienceCreate.analyzeFailure')

  const failureAlertDescription =
    isPreviewFailed || isCreateFailed
      ? isPreviewFailed
        ? firstNonEmptyMessage(
            previewSummaryMessage,
            createErrorMessage,
            createSummaryMessage,
          ) ?? analyzeFailureDefault
        : firstNonEmptyMessage(createErrorMessage, createSummaryMessage) ??
          analyzeFailureDefault
      : analyzeFailureDefault

  const failureStatusHelperDefault = t('audienceCreate.workflowIssue')

  const failureStatusHelperMessage =
    isPreviewFailed || isCreateFailed
      ? isPreviewFailed
        ? firstNonEmptyMessage(
            previewSummaryMessage,
            createErrorMessage,
            createSummaryMessage,
          ) ?? failureStatusHelperDefault
        : firstNonEmptyMessage(createErrorMessage, createSummaryMessage) ??
          failureStatusHelperDefault
      : failureStatusHelperDefault

  const primaryAlert = isPreviewFailed || isCreateFailed
    ? {
        variant: 'destructive' as const,
        title: t('audienceCreate.createFailed'),
        description: failureAlertDescription,
      }
    : isExpired
      ? { variant: 'destructive' as const, title: t('audienceCreate.sessionExpired'), description: t('audienceCreate.sessionExpiredBody') }
      : !isRealtimeConnected
        ? { variant: undefined, title: t('audienceCreate.realtimePaused'), description: t('audienceCreate.realtimePausedBody') }
        : null

  const secondaryAlerts: Array<{ title: string; description: string }> = []
  if (primaryAlert && !isRealtimeConnected && primaryAlert.title !== t('audienceCreate.realtimePaused')) {
    secondaryAlerts.push({ title: t('audienceCreate.realtimePaused'), description: t('audienceCreate.realtimePausedShort') })
  }

  if (isSimulating && isEnrichmentFailed && !actionError) {
    return (
      <>
        <DiscardImportSessionDialog
          open={discardDialogOpen}
          onOpenChange={setDiscardDialogOpen}
          onConfirm={handleDiscard}
          isPending={isDiscarding}
        />

        <div className="flex flex-1 flex-col items-center justify-center gap-6 motion-safe:animate-wizard-enter-content">
          <div className="flex w-full max-w-[768px] flex-col gap-5">
            <SimulatingProgressCard
              percent={simulatingPercent}
              title={t('audienceCreate.preparingAudience')}
              subtitle={t('audienceCreate.previewTrouble')}
              dismissHint={t('audienceCreate.dismissHint')}
              resumeHint={t('audienceCreate.resumeHint')}
              helper=""
              className="w-full"
            />
            <Alert className="rounded-2xl border-chart-warning/30 bg-chart-warning/10">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>{t('audienceCreate.enrichmentIssue')}</AlertTitle>
              <AlertDescription>
                {t('audienceCreate.enrichmentIssueBody')}
              </AlertDescription>
            </Alert>
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                type="button"
                variant="outline"
                shape="pill"
                disabled={isRetryingEnrichment}
                onClick={() => void handleRetryEnrichment()}
              >
                {isRetryingEnrichment ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('audienceCreate.retrying')}
                  </>
                ) : (
                  <>
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    {t('audienceCreate.retry')}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </>
    )
  }

  if (isSimulating && !primaryAlert && !actionError) {
    return (
      <>
        <DiscardImportSessionDialog
          open={discardDialogOpen}
          onOpenChange={setDiscardDialogOpen}
          onConfirm={handleDiscard}
          isPending={isDiscarding}
        />

        <div className="flex flex-1 flex-col items-center justify-center gap-6 motion-safe:animate-wizard-enter-content">
          <SimulatingProgressCard
            percent={simulatingPercent}
            title={
              isReviewPreparationInProgress
                ? t('audienceCreate.preparingAudience')
                : t('audienceCreate.buildingAudience')
            }
            subtitle={
              isReviewPreparationInProgress
                ? t('audienceCreate.reviewingShared')
                : t('audienceCreate.simulatingWait')
            }
            dismissHint={
              isReviewPreparationInProgress
                ? t('audienceCreate.dismissHint')
                : undefined
            }
            resumeHint={t('audienceCreate.resumeHint')}
            helper=""
            className="w-full max-w-[768px]"
          />
        </div>
      </>
    )
  }

  return (
    <>
      <DiscardImportSessionDialog
        open={discardDialogOpen}
        onOpenChange={setDiscardDialogOpen}
        onConfirm={handleDiscard}
        isPending={isDiscarding}
      />

      {actionError ? (
        <Alert variant="destructive" className="rounded-2xl">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{actionError}</AlertDescription>
        </Alert>
      ) : null}

      {isExpiringSoon ? (
        <SessionExpirySnackbar message={t('audienceCreate.sessionExpiryHint')} />
      ) : null}

      {primaryAlert ? (
        <Alert
          variant={primaryAlert.variant}
          className={primaryAlert.variant === 'destructive' ? 'rounded-2xl' : 'rounded-2xl border-chart-warning/30 bg-chart-warning/10'}
        >
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{primaryAlert.title}</AlertTitle>
          <AlertDescription>{primaryAlert.description}</AlertDescription>
        </Alert>
      ) : null}

      {secondaryAlerts.length > 0 ? (
        <details className="group rounded-xl border border-border/60 bg-muted/10">
          <summary className="flex cursor-pointer items-center gap-2 px-4 py-2.5 text-sm text-muted-foreground">
            {t('audienceCreate.moreAlerts', { count: secondaryAlerts.length })}
          </summary>
          <div className="flex flex-col gap-3 px-4 pb-4">
            {secondaryAlerts.map((alert) => (
              <Alert key={alert.title} className="rounded-2xl border-border/70 bg-muted/20">
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{alert.title}</AlertTitle>
                <AlertDescription>{alert.description}</AlertDescription>
              </Alert>
            ))}
          </div>
        </details>
      ) : null}

      <div className="flex flex-col items-center gap-5 rounded-2xl border border-border/70 bg-card/95 p-8 shadow-sm sm:p-10">
        {isCreateCompleted ? (
          <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        ) : isPreviewFailed || isCreateFailed || isExpired ? (
          <AlertCircle className="h-10 w-10 text-destructive" />
        ) : (
          <SimulatingProgressCard
            percent={simulatingPercent}
            title={isReviewPreparationInProgress ? t('audienceCreate.preparingAudience') : t('audienceCreate.buildingAudience')}
            subtitle={isReviewPreparationInProgress
              ? t('audienceCreate.reviewingShared')
              : t('audienceCreate.simulatingWait')}
            dismissHint={
              isReviewPreparationInProgress
                ? t('audienceCreate.dismissHint')
                : undefined
            }
            resumeHint={t('audienceCreate.resumeHint')}
            helper=""
            className="w-full max-w-[768px]"
          />
        )}

        {isCreateCompleted || isExpired || isPreviewFailed || isCreateFailed ? (
          <Text size="sm" weight="medium" className="text-center">
            {isCreateCompleted
              ? t('audienceCreate.createdOk')
              : isExpired
                ? t('audienceCreate.expiredSession')
                : t('audienceCreate.somethingWrong')}
          </Text>
        ) : null}

        {isCreateCompleted && audienceDetailRoute ? (
          <Button
            asChild
            variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
            shape="pill"
            className={AUDIENCE_WIZARD_BUTTON_CLASS}
          >
            <Link href={audienceDetailRoute}>{t('audienceCreate.openAudience')}</Link>
          </Button>
        ) : null}

        {createState === 'completed' && !audienceDetailRoute ? (
          <Alert className="rounded-2xl border-border/70 bg-muted/20">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t('audienceCreate.routeUnavailable')}</AlertTitle>
            <AlertDescription>
              {t('audienceCreate.routeUnavailableBody')}
            </AlertDescription>
          </Alert>
        ) : null}

        {flowActions?.canRetryCreate ? (
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              type="button"
              variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
              shape="pill"
              className={AUDIENCE_WIZARD_BUTTON_CLASS}
              disabled={isCreating}
              onClick={() => {
                setActionError(null)
                void (async () => {
                  clearPendingCreateIntent(sessionId)
                  const ack = await retryCreate()
                  if (sessionId) {
                    startAudienceHandoff({
                      sessionId,
                      sourceRoute: 'status-step',
                      trigger: 'retry_create',
                    })
                  }
                  if (
                    ack &&
                    typeof ack === 'object' &&
                    'flowSnapshot' in ack &&
                    ack.flowSnapshot?.route
                  ) {
                    const targetRoute = ack.flowSnapshot.route
                    await warmImportRouteCaches({
                      sessionId: sessionId!,
                      targetRoute,
                      ack: ack as AudienceImportRouteAck,
                    })
                    navigateToImportRoute({
                      sessionId,
                      sourceRoute: pathname,
                      targetRoute,
                      router,
                      trigger: 'retry_create',
                    })
                  }
                })().catch((retryError) => {
                  setActionError(getApiErrorMessage(retryError, t('audienceCreate.retryCreate')))
                })
              }}
            >
              {isCreating ? (
                <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('audienceCreate.retrying')}
                  </>
                ) : (
                  <>
                    <RefreshCcw className="mr-2 h-4 w-4" />
                    {t('audienceCreate.retryCreate')}
                </>
              )}
            </Button>
          </div>
        ) : actionError && flowActions?.canCreate ? (
          <div className="flex flex-wrap justify-center gap-3">
            <Button
              type="button"
              variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
              shape="pill"
              className={AUDIENCE_WIZARD_BUTTON_CLASS}
              disabled={isCreating}
              onClick={() => {
                void startCreateFromStatus(createIntentFiltersRef.current, createIntentCustomLabelsRef.current, createIntentAudienceTagsRef.current)
              }}
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('audienceCreate.creatingAudience')}
                </>
              ) : (
                <>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  {t('audienceCreate.retryCreate')}
                </>
              )}
            </Button>
          </div>
        ) : isExpired ? (
          <Button
            asChild
            variant={AUDIENCE_WIZARD_BUTTON_VARIANT}
            shape="pill"
            className={AUDIENCE_WIZARD_BUTTON_CLASS}
          >
            <Link href="/audiences/new">{t('audienceCreate.startOver')}</Link>
          </Button>
        ) : null}
      </div>

      {flowActions?.canDiscard ? (
        <Button
          type="button"
          variant="ghost"
          className="self-start text-muted-foreground hover:text-foreground"
          onClick={() => setDiscardDialogOpen(true)}
          disabled={isDiscarding}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {isDiscarding ? t('audienceCreate.discardingSession') : t('audienceCreate.discardSession')}
        </Button>
      ) : null}
    </>
  )
}
